import { NgModule, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule, RouterStateSnapshot, Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { LoggedInGuard } from './guards/loggedin.guard';
import { adminGuard, dbTemplateAdminGuard } from './guards/admin.guard';
import { unsavedChangesGuard } from './guards/unsaved-changes.guard';
import { LoginComponent } from './login/login.component';
import { MainLayoutComponent } from './main-layout/main-layout.component';
import { BrowserUtils } from '@azure/msal-browser';
import { CustomerCreateComponent } from './features/customer/customer-create/customer-create.component';
import { DashboardComponent } from './features/dashboard/dashboard.component';
import { CustomerDashboardComponent } from './features/customer/customer-dashboard/customer-dashboard.component';
import { CustomerListComponent } from './features/customer/customer-list/customer-list.component';
import { CustomerAdministrationComponent } from './features/admin/customer-administration/customer-administration.component';
import { SynchronizationListComponent } from './features/synchronization/synchronization-list/synchronization-list.component';
import { DbTemplateListComponent } from './features/admin/db-template/db-template-list/db-template-list.component';
import { TemplateListComponent } from './features/template/template-list/template-list.component';
import { ErrorPageComponent } from './common/error-page/error-page.component';
import { TemplateDetailComponent } from './features/template/template-detail/template-detail.component';
import { EventListComponent } from './features/events/event-list/event-list.component';
import { SynchronizationDetailComponent } from './features/synchronization/synchronization-detail/synchronization-detail.component';
import { ResourceAccessListComponent } from './features/resource-access/resource-access-list/resource-access-list.component';
import { ProfileMainComponent } from './features/admin/profile/profile-main/profile-main.component';
import { ProfileHomeComponent } from './features/admin/profile/profile-home/profile-home.component';
import { TranslationsListComponent } from './features/admin/translations/translations-list/translations-list.component';
import { DbTemplateDetailComponent } from './features/admin/db-template/db-template-detail/db-template-detail.component';
import { AdminDashboardComponent } from './features/admin-dashboard/admin-dashboard.component';
import { ErrorPlaygroundComponent } from './common/errors/error-playground/error-playground.component';
import { SynchronizationMonitoringComponent } from './features/synchronization/synchronization-monitoring/synchronization-monitoring.component';
import { environment } from '../environments/environment';

const routes: Routes = [
  { path: 'error', component: ErrorPageComponent, title: 'Fejl' },
  { path: 'login', component: LoginComponent, canActivate: [MsalGuard], title: 'Log ind' },
  {
    path: '',
    component: MainLayoutComponent, canActivate: [(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(LoggedInGuard).canActivate(next, state)],
    children: [
      // Main routes.
      { path: 'home', component: DashboardComponent, title: 'Dashboard' },
      { path: 'admin-dashboard', component: AdminDashboardComponent, canActivate: [adminGuard], title: 'Admin Dashboard' },
      { path: 'customers', component: CustomerListComponent, title: 'Kunder' },
      { path: 'customer-create', component: CustomerCreateComponent, title: 'Opret kunde' },
      { path: 'customer-administration', component: CustomerAdministrationComponent, canActivate: [adminGuard], title: 'Kunde administration' },
      { path: 'customer-dashboard/:kundeid', component: CustomerDashboardComponent, title: 'Kunde dashboard' },
      // { path: 'companies', component: CompanyListComponent, title: 'Virksomheder' },
      { path: 'db-templates', component: DbTemplateListComponent, canActivate: [dbTemplateAdminGuard], title: 'Database skabeloner' },
      { path: 'db-template/:templateid', component: DbTemplateDetailComponent, canActivate: [dbTemplateAdminGuard], canDeactivate: [unsavedChangesGuard], title: 'Database skabelon' },
      // Synkroniseringscenteret is the admin dashboard's first tab now; old links keep working.
      { path: 'synccenter', redirectTo: 'admin-dashboard' },
      { path: 'templates', component: TemplateListComponent, title: 'Skabeloner' },
      { path: 'template/:templateid', component: TemplateDetailComponent, canDeactivate: [unsavedChangesGuard], title: 'Skabelon' },
      {
        path: 'synchronizations',
        children: [
          { path: '', component: SynchronizationListComponent, title: 'Synkroniseringer' },
          { path: 'monitoring', component: SynchronizationMonitoringComponent, title: 'Overvågning' },
        ]
      },
      { path: 'synchronization/:synccontractid', component: SynchronizationDetailComponent, title: 'Synkronisering' },
      { path: 'events', component: EventListComponent, title: 'Hændelser' },
      { path: 'events/:eventid', component: EventListComponent, title: 'Hændelse' },
      { path: 'resourceaccess', component: ResourceAccessListComponent, title: 'Adgange' },
      { path: 'resourceaccess/:searchterm', component: ResourceAccessListComponent, title: 'Adgange' },
      { path: 'translations', component: TranslationsListComponent, canActivate: [adminGuard], title: 'Oversættelser' },
      {
        path: 'profile',
        component: ProfileMainComponent,
        title: 'Profil',
        children: [
          {
            path: 'home', 
            component: ProfileHomeComponent,
            title: 'Profil oversigt'
          },
          {
            path: '', 
            redirectTo: 'home',
            pathMatch: 'full'
          }
        ]
      },
      // Dev-only error-handling playground; not registered in production builds.
      ...(!environment.production
        ? [{ path: 'dev/errors', component: ErrorPlaygroundComponent, title: 'Error Playground' }]
        : []),
      // Disse linjer SKAL stå sidst
      { path: '**', redirectTo: 'home' },
    ]
  },

];

@NgModule({
  imports: [RouterModule.forRoot(routes, { 
    // Don't perform initial navigation in iframes or popups
    initialNavigation: !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup() ? 'enabledNonBlocking' : 'disabled' // Set to enabledBlocking to use Angular Universal  
  })],
  exports: [RouterModule]

})
export class AppRoutingModule { }

